import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DEFAULT_GROUPS_FILTERS,
  GroupsFiltersPopover,
  GroupsFiltersState,
} from "./GroupsFiltersPopover";
import { GroupsTable } from "./GroupsTable";
import {
  useAccessGroupFilterOptions,
  useAccessGroups,
  useUpdateAccessGroupStatus,
} from "../hooks/useAccessGroups";
import { useSorting } from "@/hooks/useSorting";
import { UsersTabKey } from "@/components/pages/users/usersPageConfig";
import type {
  AccessGroupListQuery,
  AccessGroupScope,
  AccessGroupSortField,
} from "@/types/access-group";

const PER_PAGE = 10;

const buildGroupsReturnPath = (pathname: string, search: string): string => {
  const params = new URLSearchParams(search);
  params.set("tab", UsersTabKey.GROUPS);
  return `${pathname}?${params.toString()}`;
};

const resolveAccessGroupSortField = (
  value: string | null,
): AccessGroupSortField | undefined => {
  switch (value) {
    case "isActive":
    case "name":
    case "hotelCount":
    case "tenantCount":
    case "memberCount":
    case "createdAt":
    case "updatedAt":
      return value;
    default:
      return undefined;
  }
};

type GroupsTabProps = {
  backTo: string | null;
  canAddGroup: boolean;
  canEditGroup: boolean;
  canViewGroup: boolean;
  scope: AccessGroupScope;
};

export const GroupsTab: React.FC<GroupsTabProps> = ({
  backTo,
  canAddGroup,
  canEditGroup,
  canViewGroup,
  scope,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const groupsReturnPath = buildGroupsReturnPath(
    location.pathname,
    location.search,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [groupsFilters, setGroupsFilters] = useState<GroupsFiltersState>(
    DEFAULT_GROUPS_FILTERS,
  );
  const [updatingGroupId, setUpdatingGroupId] = useState<string | null>(null);
  const { sortField, sortDirection, handleSort } = useSorting();
  const { data: filterOptions } = useAccessGroupFilterOptions(scope);
  const query: AccessGroupListQuery = {
    currentPage,
    perPage: PER_PAGE,
    sortField: resolveAccessGroupSortField(sortField),
    sortDirection: sortDirection ?? undefined,
    hotelIds:
      scope === "tenant" && groupsFilters.entityIds.length > 0
        ? groupsFilters.entityIds
        : undefined,
    tenantIds:
      scope === "admin" && groupsFilters.entityIds.length > 0
        ? groupsFilters.entityIds
        : undefined,
    userIds:
      groupsFilters.userIds.length > 0 ? groupsFilters.userIds : undefined,
  };
  const { data: groupsResponse, isLoading } = useAccessGroups(scope, query);
  const { mutateAsync: updateAccessGroupStatus } = useUpdateAccessGroupStatus();
  const groups = groupsResponse?.items ?? [];
  const totalPages = Math.max(
    1,
    Math.ceil((groupsResponse?.total ?? 0) / PER_PAGE),
  );

  const handleGroupSort = (field: string) => {
    setCurrentPage(1);
    handleSort(field);
  };

  // Both the row click (read-only preview) and the Edit action open the same
  // group screen; the screen decides editability from the user's permissions.
  const navigateToGroup = useCallback(
    (id: string) => {
      const params = new URLSearchParams();
      params.set("backTo", groupsReturnPath);
      navigate(`/users/groups/${id}/edit?${params.toString()}`);
    },
    [groupsReturnPath, navigate],
  );

  const handleToggleStatus = useCallback(
    async (id: string, isActive: boolean) => {
      setUpdatingGroupId(id);
      try {
        await updateAccessGroupStatus({ id, isActive });
        toast.success(
          isActive
            ? t("users.groups.table.activatedSuccessfully")
            : t("users.groups.table.deactivatedSuccessfully"),
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : undefined;
        toast.error(message ?? t("users.groups.table.statusUpdateFailed"));
      } finally {
        setUpdatingGroupId((current) => (current === id ? null : current));
      }
    },
    [t, updateAccessGroupStatus],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {backTo ? (
            <Button variant="outline" onClick={() => navigate(backTo)}>
              {t("users.page.back")}
            </Button>
          ) : null}
          <GroupsFiltersPopover
            applied={groupsFilters}
            onApply={(next) => {
              setGroupsFilters(next);
              setCurrentPage(1);
            }}
            scope={scope}
            entityOptions={filterOptions?.entities ?? []}
            userOptions={filterOptions?.users ?? []}
          />
        </div>

        <Button
          variant="new_default"
          size="default"
          className="h-8 gap-1 rounded-md bg-[#826BFF] px-4 py-2 text-white hover:bg-[#725BE8]"
          onClick={() => {
            const params = new URLSearchParams();
            params.set("backTo", groupsReturnPath);
            params.set("scope", scope);
            navigate(`/users/groups/new?${params.toString()}`);
          }}
          disabled={!canAddGroup}
          title={
            !canAddGroup
              ? t("common.insufficientPermissions", "Insufficient permissions")
              : undefined
          }
        >
          <Plus className="h-4 w-4" />
          {t("users.page.addGroup")}
        </Button>
      </div>

      <GroupsTable
        rows={groups}
        isLoading={isLoading}
        scope={scope}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleGroupSort}
        canEdit={canEditGroup}
        onEdit={navigateToGroup}
        updatingGroupId={updatingGroupId}
        onToggleStatus={scope === "tenant" ? handleToggleStatus : undefined}
        onRowClick={canViewGroup ? navigateToGroup : undefined}
      />
    </div>
  );
};
